import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { useApi } from "@/lib/api";
import {
  cleanMarkdownBody,
  extractTitleAndSubtitle,
  splitOnHr,
} from "@/components/page";
import {
  EditorialDivider,
  EditorialFooter,
  EditorialMarkdownSection,
} from "@/components/editorial";
import { cn } from "@/lib/utils";

type Session = {
  id: string;
  scheduled_at: string;
  summary: string | null;
  created_at: string;
};

type ClientDetail = {
  id: string;
  name: string;
  sessions: Session[];
};

const dateLongFmt = new Intl.DateTimeFormat("he-IL", {
  weekday: "long",
  day: "2-digit",
  month: "long",
  year: "numeric",
});

export function SessionDetailPage() {
  const { clientId, sessionId } = useParams<{
    clientId: string;
    sessionId: string;
  }>();
  const api = useApi();
  const [client, setClient] = useState<ClientDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!clientId) return;
      setLoading(true);
      setError(null);
      try {
        const res = await api(`/api/clients/${clientId}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as ClientDetail;
        if (!cancelled) setClient(data);
      } catch (e) {
        if (!cancelled)
          setError(e instanceof Error ? e.message : "שגיאה בטעינה");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const { session, index, total, prev, next } = useMemo(() => {
    if (!client) {
      return {
        session: null,
        index: -1,
        total: 0,
        prev: null,
        next: null,
      } as const;
    }
    const idx = client.sessions.findIndex((s) => s.id === sessionId);
    return {
      session: idx >= 0 ? client.sessions[idx] : null,
      index: idx,
      total: client.sessions.length,
      prev: idx >= 0 ? client.sessions[idx + 1] ?? null : null,
      next: idx >= 0 ? client.sessions[idx - 1] ?? null : null,
    } as const;
  }, [client, sessionId]);

  const meta = useMemo(
    () => (session ? extractTitleAndSubtitle(session.summary) : null),
    [session],
  );

  /* Pre-parse the markdown into sections so we can render them with
     stable ids, and also build the right-hand index from the per-chunk
     headings. */
  const sections = useMemo(() => {
    if (!session) return [];
    const body = cleanMarkdownBody(session.summary);
    if (!body) return [];
    return splitOnHr(body)
      .map((c) => c.trim())
      .filter((c) => c.length > 0)
      .map((chunk, idx) => {
        const { title } = extractTitleAndSubtitle(chunk);
        return {
          id: `section-${idx}`,
          title: title || `חלק ${idx + 1}`,
          chunk,
        };
      });
  }, [session]);

  if (loading && !client) {
    return (
      <PageFrame>
        <p className="text-sm text-stone-500">טוענת את המפגש…</p>
      </PageFrame>
    );
  }

  if (error || !client || !session || !meta) {
    return (
      <PageFrame>
        <h1 className="text-3xl font-bold tracking-tight text-stone-900">
          המפגש לא נמצא
        </h1>
        <p className="mt-3 text-sm text-stone-600">
          {error ?? "לא נמצא מפגש לפי המזהה."}
        </p>
        <Link
          to={clientId ? `/clients/${clientId}` : "/clients"}
          className="mt-6 inline-flex items-center gap-2 text-sm text-stone-700 hover:text-stone-900"
        >
          <ArrowRight className="h-4 w-4" />
          חזרה לעמוד המתאמנת
        </Link>
      </PageFrame>
    );
  }

  const sessionNumber = total - index;
  const date = new Date(session.scheduled_at);

  return (
    <div className="-mx-6 -my-8 px-6 py-6 sm:py-8 bg-stone-50 min-h-svh">
      <div className="mx-auto max-w-6xl">
        {/* Back link */}
        <Link
          to={`/clients/${clientId}`}
          className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-stone-900 transition-colors"
        >
          <ArrowRight className="h-4 w-4" />
          חזרה ל{client.name}
        </Link>

        {/* Editorial hero */}
        <header className="mt-7 pb-7 border-b border-stone-200/80">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-stone-500">
            מפגש #{sessionNumber} <span className="opacity-60">·</span>{" "}
            <span dir="ltr">{dateLongFmt.format(date)}</span>
          </p>
          <h1 className="mt-4 text-4xl sm:text-5xl font-bold tracking-[-0.03em] leading-[1.05] text-stone-900 max-w-3xl">
            {meta.title || `מפגש מ־${dateLongFmt.format(date)}`}
          </h1>
          {meta.subtitle ? (
            <p className="mt-4 text-lg text-stone-600 leading-relaxed max-w-2xl">
              {meta.subtitle}
            </p>
          ) : null}
        </header>

        {/* Two-column body: index on the right (RTL natural), content on the left */}
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-[1fr_220px] gap-10 lg:gap-14">
          {/* Content column — left in RTL */}
          <article className="min-w-0 max-w-3xl prose prose-stone prose-stone-editorial">
            {sections.length > 0 ? (
              <div>
                {sections.map((s, idx) => (
                  <div key={s.id}>
                    {idx > 0 ? <EditorialDivider /> : null}
                    <EditorialMarkdownSection
                      index={idx + 1}
                      title={s.title}
                      body={s.chunk}
                      id={s.id}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-stone-500 italic">
                לא נשמר סיכום למפגש הזה.
              </p>
            )}
          </article>

          {/* Index column — right in RTL */}
          {sections.length > 1 ? (
            <SessionIndex sections={sections} />
          ) : (
            <div />
          )}
        </div>

        {/* Editorial credit — session metadata */}
        <EditorialFooter
          primary={
            <>
              {client.name} · מפגש #{sessionNumber}
            </>
          }
          secondary={
            <span dir="ltr">{dateLongFmt.format(date)}</span>
          }
        />

        {/* Prev / next */}
        <nav className="mt-10 pt-6 border-t border-stone-200/80 flex items-center justify-between gap-4 text-sm">
          {prev ? (
            <SessionNavLink
              to={`/clients/${clientId}/sessions/${prev.id}`}
              direction="prev"
              label="המפגש הקודם"
              session={prev}
              total={total}
              indexInList={
                client.sessions.findIndex((s) => s.id === prev.id)
              }
            />
          ) : (
            <span />
          )}
          {next ? (
            <SessionNavLink
              to={`/clients/${clientId}/sessions/${next.id}`}
              direction="next"
              label="המפגש הבא"
              session={next}
              total={total}
              indexInList={
                client.sessions.findIndex((s) => s.id === next.id)
              }
            />
          ) : (
            <span />
          )}
        </nav>
      </div>
    </div>
  );
}

/* ---------- Right-hand section index ---------- */

function SessionIndex({
  sections,
}: {
  sections: { id: string; title: string }[];
}) {
  const [activeId, setActiveId] = useState<string>(sections[0]?.id ?? "");
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    const elements = sections
      .map((s) => document.getElementById(s.id))
      .filter((el): el is HTMLElement => !!el);

    if (elements.length === 0) return;

    observerRef.current?.disconnect();
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort(
            (a, b) =>
              (a.target as HTMLElement).offsetTop -
              (b.target as HTMLElement).offsetTop,
          );
        if (visible.length > 0) {
          setActiveId(visible[0].target.id);
        }
      },
      {
        rootMargin: "-20% 0px -60% 0px",
        threshold: [0, 0.25, 0.5, 1],
      },
    );

    elements.forEach((el) => obs.observe(el));
    observerRef.current = obs;
    return () => obs.disconnect();
  }, [sections]);

  const handleClick = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setActiveId(id);
    }
  };

  return (
    <aside className="lg:sticky lg:top-10 lg:self-start">
      <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-stone-400 mb-5">
        חלקים
      </p>
      <ol className="space-y-1">
        {sections.map((s, idx) => {
          const active = s.id === activeId;
          const num = String(idx + 1).padStart(2, "0");
          return (
            <li key={s.id}>
              <a
                href={`#${s.id}`}
                onClick={(e) => handleClick(e, s.id)}
                className={cn(
                  "group flex items-baseline gap-3 py-2 pe-1 transition-colors",
                  active ? "text-stone-900" : "text-stone-400 hover:text-stone-700",
                )}
              >
                <span
                  className={cn(
                    "text-[10px] font-semibold tracking-[0.18em] tabular-nums shrink-0 mt-0.5",
                    active ? "text-stone-900" : "text-stone-400",
                  )}
                  dir="ltr"
                >
                  {num}
                </span>
                <span
                  className={cn(
                    "text-[13px] leading-snug line-clamp-2 transition-all",
                    active ? "font-semibold" : "font-normal",
                  )}
                >
                  {s.title}
                </span>
              </a>
            </li>
          );
        })}
      </ol>
    </aside>
  );
}

function SessionNavLink({
  to,
  direction,
  label,
  session,
  total,
  indexInList,
}: {
  to: string;
  direction: "prev" | "next";
  label: string;
  session: Session;
  total: number;
  indexInList: number;
}) {
  const meta = extractTitleAndSubtitle(session.summary);
  const number = total - indexInList;
  const Chevron = direction === "next" ? ChevronLeft : ChevronRight;
  return (
    <Link
      to={to}
      className={`inline-flex items-center gap-3 group max-w-[45%] ${
        direction === "next" ? "ms-auto" : ""
      }`}
    >
      {direction === "prev" ? (
        <Chevron className="h-4 w-4 shrink-0 text-stone-400 group-hover:text-stone-700 transition-colors" />
      ) : null}
      <span
        className={`flex flex-col min-w-0 ${
          direction === "next" ? "text-end" : ""
        }`}
      >
        <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">
          {label} · #{number}
        </span>
        <span className="mt-0.5 text-sm font-medium text-stone-800 truncate">
          {meta.title ||
            new Intl.DateTimeFormat("he-IL", {
              day: "2-digit",
              month: "short",
            }).format(new Date(session.scheduled_at))}
        </span>
      </span>
      {direction === "next" ? (
        <Chevron className="h-4 w-4 shrink-0 text-stone-400 group-hover:text-stone-700 transition-colors" />
      ) : null}
    </Link>
  );
}

function PageFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="-mx-6 -my-8 px-6 py-8 bg-stone-50 min-h-svh">
      <div className="mx-auto max-w-3xl">{children}</div>
    </div>
  );
}
